import React from 'react';
import Particles from './Particles';
import useInView from '../hooks/useInView';
import useCounter from '../hooks/useCounter';
import { IMAGES } from '../data';

const HeroSection = ({ dark, t }) => {
  const [ref, inView] = useInView(0.05);
  const loans  = useCounter(5000, 2400, inView);
  const banks  = useCounter(15,   1800, inView);
  const states = useCounter(25,   2000, inView);
  const rate   = useCounter(98,   2200, inView);

  const stats = [
    { val: `${loans}+`,  label: 'Loans Disbursed' },
    { val: `${banks}+`,  label: 'Partner Banks'   },
    { val: `${states}+`, label: 'States Covered'  },
    { val: `${rate}%`,   label: 'Approval Rate'   },
  ];

  return (
    <section
      id="home"
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: dark
          ? 'radial-gradient(ellipse at 20% 50%, rgba(201,162,39,0.07) 0%, transparent 60%), #080808'
          : 'radial-gradient(ellipse at 20% 50%, rgba(201,162,39,0.12) 0%, transparent 60%), #fefaf0',
      }}
    >
      <Particles />

      {/* BG hero image overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: dark ? 0.04 : 0.05,
        }}
      />

      {/* Diagonal accent */}
      <div
        style={{
          position: 'absolute',
          top: 0, right: 0,
          width: '45%', height: '100%',
          zIndex: 1,
          background: dark
            ? 'linear-gradient(130deg,transparent 0%,rgba(201,162,39,0.03) 100%)'
            : 'linear-gradient(130deg,transparent 0%,rgba(201,162,39,0.06) 100%)',
          clipPath: 'polygon(25% 0,100% 0,100% 100%,0 100%)',
        }}
      />

      {/* Content */}
      <div
        ref={ref}
        className="two-col"
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '100px 32px 60px',
          minHeight: '100vh',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          alignItems: 'center',
          gap: 60,
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* ── LEFT ── */}
        <div
          style={{
            opacity: inView ? 1 : 0,
            animation: inView ? 'fadeUp 0.8s 0.1s cubic-bezier(.16,1,.3,1) forwards' : 'none',
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: t.goldDim,
              border: '1px solid rgba(201,162,39,0.3)',
              borderRadius: 40,
              padding: '8px 20px',
              marginBottom: 32,
            }}
          >
            <span
              style={{
                width: 8, height: 8,
                borderRadius: '50%',
                background: '#c9a227',
                display: 'inline-block',
                animation: 'dotPulse 2s infinite',
              }}
            />
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                color: t.gold,
                letterSpacing: 1.5,
                fontWeight: 600,
              }}
            >
              TRUSTED LOAN PARTNER ACROSS INDIA
            </span>
          </div>

          {/* Headline */}
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 700,
              lineHeight: 1.08,
              marginBottom: 28,
              fontSize: 'clamp(2.8rem,6vw,5rem)',
              color: t.text,
            }}
          >
            Your Financial
            <br />
            <span className="gold-text">Future Starts</span>
            <br />
            Here.
          </h1>

          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 18,
              lineHeight: 1.75,
              color: t.textSub,
              marginBottom: 40,
              maxWidth: 500,
            }}
          >
            Minimum documentation. Fully online. Fast disbursement. We bring
            India's best loan rates from top banks and NBFCs right to your
            doorstep — from villages to metro cities.
          </p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 56 }}>
            <a
              href="#contact"
              style={{
                background: 'linear-gradient(135deg,#c9a227,#e8c040)',
                color: '#080808',
                padding: '16px 40px',
                borderRadius: 8,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                textDecoration: 'none',
                boxShadow: '0 8px 32px rgba(201,162,39,0.45)',
                transition: 'all 0.3s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(201,162,39,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(201,162,39,0.45)';
              }}
            >
              Apply For Loan →
            </a>
            <a
              href="#services"
              style={{
                background: 'transparent',
                color: t.gold,
                padding: '16px 40px',
                borderRadius: 8,
                border: '1.5px solid rgba(201,162,39,0.45)',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 16,
                textDecoration: 'none',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(201,162,39,0.1)';
                e.currentTarget.style.borderColor = '#c9a227';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(201,162,39,0.45)';
              }}
            >
              Explore Services
            </a>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {stats.map((s, i) => (
              <div
                key={s.label}
                style={{
                  opacity: inView ? 1 : 0,
                  animation: inView
                    ? `fadeUp 0.6s ${0.3 + i * 0.1}s cubic-bezier(.16,1,.3,1) forwards`
                    : 'none',
                }}
              >
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: '2.1rem',
                    fontWeight: 700,
                    color: t.gold,
                    lineHeight: 1,
                  }}
                >
                  {s.val}
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    color: t.textMuted,
                    marginTop: 4,
                    letterSpacing: 0.5,
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT (hidden on mobile) ── */}
        <div
          className="hero-right"
          style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 500,
              opacity: inView ? 1 : 0,
              animation: inView ? 'slideLeft 0.9s 0.3s cubic-bezier(.16,1,.3,1) forwards' : 'none',
            }}
          >
            {/* Glow */}
            <div
              style={{
                position: 'absolute',
                inset: -24,
                borderRadius: 32,
                background: 'radial-gradient(ellipse at center,rgba(201,162,39,0.18) 0%,transparent 70%)',
                filter: 'blur(20px)',
              }}
            />

            {/* Main image */}
            <img
              src={IMAGES.hero}
              alt="Modern financial building"
              style={{
                width: '100%',
                height: 520,
                objectFit: 'cover',
                borderRadius: 24,
                border: `1px solid ${t.border}`,
                boxShadow: dark
                  ? '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,162,39,0.2)'
                  : '0 32px 80px rgba(0,0,0,0.15)',
                position: 'relative',
                zIndex: 2,
              }}
            />

            {/* Floating card – Disbursed */}
            <div
              style={{
                position: 'absolute',
                bottom: 48,
                left: -40,
                zIndex: 10,
                background: dark ? 'rgba(20,20,20,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${t.border}`,
                borderRadius: 16,
                padding: '18px 22px',
                boxShadow: dark ? '0 24px 60px rgba(0,0,0,0.6)' : '0 24px 60px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                animation: 'float 3s ease-in-out infinite',
                minWidth: 210,
              }}
            >
              <div
                style={{
                  width: 44, height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#c9a227,#e8c040)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                  animation: 'pulse 2s infinite',
                }}
              >
                ⚡
              </div>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 19, color: t.gold }}>
                  ₹ 2.4 Cr
                </div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.textMuted }}>
                  Disbursed Today
                </div>
              </div>
            </div>

            {/* Floating card – Approval */}
            <div
              style={{
                position: 'absolute',
                top: 32, right: -32,
                zIndex: 10,
                background: dark ? 'rgba(20,20,20,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${t.border}`,
                borderRadius: 16,
                padding: '16px 20px',
                boxShadow: dark ? '0 16px 40px rgba(0,0,0,0.5)' : '0 16px 40px rgba(0,0,0,0.1)',
                animation: 'float 3.5s 1s ease-in-out infinite',
              }}
            >
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.textMuted, marginBottom: 4 }}>
                Approval Time
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 22, color: t.gold }}>
                24 Hours
              </div>
              <div style={{ display: 'flex', gap: 1, marginTop: 4 }}>
                {'★★★★★'.split('').map((s, i) => (
                  <span key={i} style={{ color: '#c9a227', fontSize: 14 }}>{s}</span>
                ))}
              </div>
            </div>

            {/* Decorative border */}
            <div
              style={{
                position: 'absolute',
                bottom: -16, right: -16,
                width: '55%', height: '55%',
                border: `2px solid ${t.border}`,
                borderRadius: 24,
                zIndex: 0,
                animation: 'borderGlow 3s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 28, left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          zIndex: 10, opacity: 0.45,
        }}
      >
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: t.textMuted, letterSpacing: 2, textTransform: 'uppercase' }}>
          Scroll
        </div>
        <div
          style={{
            width: 1.5, height: 36,
            background: `linear-gradient(to bottom,${t.gold},transparent)`,
            animation: 'float 1.5s ease-in-out infinite',
          }}
        />
      </div>
    </section>
  );
};

export default HeroSection;
